import { useState } from "react"
import { ArrowLeft, Calendar, Clock, Users, CheckCircle2, ExternalLink, Linkedin, Twitter, Mail } from "lucide-react"
import { useTheme } from "@/clientWeb/contexts/ThemeContext"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { Button } from "@/clientWeb/components/ui/button"
import { Input } from "@/clientWeb/components/ui/input"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/clientWeb/components/ui/dropdown-menu"

interface WebinarRegistrationProps {
  onBack: () => void
}

export function WebinarRegistration({ onBack }: WebinarRegistrationProps) {
  const t = useTranslation();
  const { theme } = useTheme()
  const [fullName, setFullName] = useState("")
  const [email, setEmail] = useState("")
  const [errors, setErrors] = useState<{ fullName?: string; email?: string }>({})
  const [isRegistered, setIsRegistered] = useState(false)
  const countdown = {
    hours: 0,
    minutes: 0,
    seconds: 12,
  }

  const handleContinue = (e: React.FormEvent) => {
    e.preventDefault()
    const next: { fullName?: string; email?: string } = {}
    if (fullName.trim().length < 2) next.fullName = 'Full name must be at least 2 characters'
    if (!email.trim()) next.email = 'Email is required'
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) next.email = 'Invalid email address'
    if (Object.keys(next).length) {
      setErrors(next)
      return
    }
    setErrors({})
    setIsRegistered(true)
  }

  const handleJoinWebinar = () => {
    // Stub — real webinar join flow plugs in here once API lands.
  }

  if (isRegistered) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        {/* Header */}
        <header className="bg-card/80 pl-6 justify-between flex items-center backdrop-blur-sm border-b border-border sticky top-0 z-10">
        <div className="w-24">
          <img src={theme === 'dark' ? '/InmeetWhiteLogo.svg' : '/Inmeet.svg'} alt=""/>
        </div>
          <div className="sm:px-6 py-4">
            <Button
              onClick={onBack}
              variant="ghost"
              className="group"
            >
              <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
              <span className="font-medium">{t("webinarRegistration.backToDashboard")}</span>
            </Button>
          </div>
        </header>

        {/* Main Content */}
        <main className="px-4 sm:px-6 lg:px-4 py-4 sm:py-4 overflow-y-auto">
          <div className="grid lg:grid-cols-[1.2fr_0.8fr] gap-8 lg:gap-12 justify-center items-center h-full">
            {/* Left: Video/Speaker Section */}
            <div className="space-y-4 pl-0 sm:pl-10">
              {/* Speaker Info */}
              <div className="p-2">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-xl shadow-lg">
                    D
                  </div>
                  <div className="flex-1 flex items-center gap-4">
                    <div className="flex flex-col">
                    <h3 className="text-lg font-semibold text-foreground">{t("webinarRegistration.speaker")}</h3>
                    <p className="text-muted-foreground">Dvvw</p>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="link" className="h-auto px-0 py-0 text-sm flex items-center gap-1">
                          {t("webinarRegistration.viewFullBio")}
                          <ExternalLink className="w-3 h-3" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="w-80 p-0" align="start">
                        {/* Speaker Header */}
                        <div className="p-4 border-b border-border">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold shadow-lg">
                              D
                            </div>
                            <div>
                              <h4 className="font-semibold text-foreground">Dvvw</h4>
                              <p className="text-sm text-muted-foreground">Senior Web Developer</p>
                            </div>
                          </div>
                        </div>

                        {/* Bio Content */}
                        <div className="p-4 space-y-3">
                          <DropdownMenuLabel className="text-xs font-semibold text-foreground px-0">
                            {t("webinarRegistration.about")}
                          </DropdownMenuLabel>
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            Passionate web developer with 10+ years of experience in modern web technologies. 
                            Specialized in React, TypeScript, and scalable architecture. 
                            Love sharing knowledge and helping developers grow.
                          </p>
                          
                          <DropdownMenuLabel className="text-xs font-semibold text-foreground px-0 pt-2">
                            {t("webinarRegistration.expertise")}
                          </DropdownMenuLabel>
                          <div className="flex flex-wrap gap-1">
                            <span className="px-2 py-1 bg-slate-100 dark:bg-muted text-xs rounded-md text-slate-700 dark:text-foreground">React</span>
                            <span className="px-2 py-1 bg-slate-100 dark:bg-muted text-xs rounded-md text-slate-700 dark:text-foreground">TypeScript</span>
                            <span className="px-2 py-1 bg-slate-100 dark:bg-muted text-xs rounded-md text-slate-700 dark:text-foreground">Node.js</span>
                            <span className="px-2 py-1 bg-slate-100 dark:bg-muted text-xs rounded-md text-slate-700 dark:text-foreground">UI/UX</span>
                          </div>
                        </div>

                        <DropdownMenuSeparator />

                        {/* Social Links */}
                        <div className="p-2">
                          <DropdownMenuItem className="flex items-center gap-3 px-3 py-2 cursor-pointer">
                            <Linkedin className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm">{t("webinarRegistration.linkedInProfile")}</span>
                          </DropdownMenuItem>
                          <DropdownMenuItem className="flex items-center gap-3 px-3 py-2 cursor-pointer">
                            <Twitter className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm">{t("webinarRegistration.twitterProfile")}</span>
                          </DropdownMenuItem>
                          <DropdownMenuItem className="flex items-center gap-3 px-3 py-2 cursor-pointer">
                            <Mail className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm">{t("webinarRegistration.contactSpeaker")}</span>
                          </DropdownMenuItem>
                        </div>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </div>

              {/* Video Preview */}
              <div className="bg-gradient-to-br from-purple-900 via-indigo-900 to-purple-900 rounded-2xl shadow-2xl overflow-hidden aspect-video relative">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center space-y-6 px-8">
                    <div className="space-y-2">
                      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white/90 tracking-tight">
                        CONFERENCE
                      </h1>
                      <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white/80">
                        WEBINAR
                      </h2>
                      <h3 className="text-5xl sm:text-6xl lg:text-7xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                        ONLINE
                      </h3>
                    </div>
                    
                    {/* Decorative elements */}
                    <div className="absolute top-10 left-10 w-20 h-20 bg-purple-500/20 rounded-full blur-2xl animate-pulse"></div>
                    <div className="absolute bottom-10 right-10 w-32 h-32 bg-pink-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
                  </div>
                </div>

                {/* Video controls placeholder */}
                <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-white/80 text-sm">
                    <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                    <span>{t("webinarRegistration.preview")}</span>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="bg-card rounded-2xl shadow-lg px-6 py-4 border border-border">
                <h3 className="text-lg font-semibold text-foreground mb-3">{t("webinarRegistration.descriptionAbout")}</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Join us for an insightful session covering the latest trends and best practices in modern web development. 
                  Our expert speaker will share valuable insights and answer your questions in real-time.
                </p>
              </div>
            </div>

            {/* Right: Registration Success & Countdown */}
            <div className="flex flex-col justify-center items-center space-y-6 px-6">
              {/* Success Message */}
              <div className="pt-8">
                <div className="text-center space-y-4">
                  <div className="relative inline-flex items-center justify-center w-16 h-16 bg-green-500 rounded-full animate-[pop_0.6s_ease-out]">
  <span className="absolute inset-0 rounded-full bg-green-400 animate-ping opacity-40"></span>
  <CheckCircle2 className="w-8 h-8 text-white relative z-10" />
</div>
                  <div>
                    <h2 className="text-2xl font-bold text-foreground mb-2">{t("webinarRegistration.registrationSuccessful")}</h2>
                  </div>
                </div>
              </div>

              {/* Countdown Timer */}
              <div className="p-2 w-full">
                <h3 className="text-lg font-semibold text-foreground mb-6 text-center">
                  Hello, {fullName.split(' ')[0]}
                </h3>
                <p className="text-center text-slate-600 dark:text-muted-foreground mb-4">{t("webinarRegistration.stayTuned")}</p>
                
                <div className="flex justify-center gap-4 mb-4">
                  <div className="text-center">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 bg-primary-100/10 text-black dark:text-foreground rounded-xl flex items-center justify-center shadow-sm">
                      <span className="text-2xl sm:text-3xl font-bold">
                        {String(countdown.hours).padStart(2, '0')}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 dark:text-muted-foreground mt-2">{t("webinarRegistration.hrs")}</p>
                  </div>
                  <div className="flex items-center">
                    <span className="text-2xl font-bold text-slate-400 dark:text-muted-foreground">:</span>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 bg-primary-100/10 text-black dark:text-foreground rounded-xl flex items-center justify-center shadow-sm">
                      <span className="text-2xl sm:text-3xl font-bold">
                        {String(countdown.minutes).padStart(2, '0')}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 dark:text-muted-foreground mt-2">{t("webinarRegistration.mins")}</p>
                  </div>
                  <div className="flex items-center">
                    <span className="text-2xl font-bold text-slate-400 dark:text-muted-foreground">:</span>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 bg-primary-100/10 text-black dark:text-foreground rounded-xl flex items-center justify-center shadow-sm">
                      <span className="text-2xl sm:text-3xl font-bold">
                        {String(countdown.seconds).padStart(2, '0')}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 dark:text-muted-foreground mt-2">{t("webinarRegistration.secs")}</p>
                  </div>
                </div>

                <Button
                  onClick={handleJoinWebinar}
                  variant={'default'}
                  className="w-full h-12"
                >
                  {t("webinarRegistration.joinWebinar")}
                </Button>
              </div>

              {/* Event Details */}
              <div className="space-y-4 w-full">
                <div className="flex items-center gap-3 text-slate-700 dark:text-foreground">
                  <Calendar className="w-5 h-5 text-primary" />
                  <span className="text-sm">10:45 AM - Mar 13, 2026, GMT+5:30</span>
                </div>
                <div className="flex items-center gap-3 text-slate-700 dark:text-foreground">
                  <Clock className="w-5 h-5 text-primary" />
                  <span className="text-sm">Duration: 60 minutes</span>
                </div>
                <div className="flex items-center gap-3 text-slate-700 dark:text-foreground">
                  <Users className="w-5 h-5 text-primary" />
                  <span className="text-sm">234 registered attendees</span>
                </div>
              </div>

              {/* Footer Note */}
              <div className="text-center text-sm text-slate-500 dark:text-muted-foreground">
                If not yet? <a href="#" className="text-primary hover:underline font-medium">Click here</a> to register
              </div>
            </div>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="bg-card/80 pl-4 flex w-full items-center justify-between backdrop-blur-sm border-b border-border sticky top-0 z-10">
        <div className="w-24">
          <img src={theme === 'dark' ? '/InmeetWhiteLogo.svg' : '/Inmeet.svg'} alt=""/>
        </div>
        <div className="px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-10 justify-between">
        
          
          <div className="flex items-center gap-4">
            <div className="hidden sm:flex items-center gap-2 text-sm text-muted-foreground">
              <Calendar className="w-4 h-4" />
              <span>Friday, March 13, 2026</span>
            </div>
            <div className="flex items-center gap-2 px-3 pt-2 bg-muted border border-border rounded-lg">
              <div className="flex gap-1">
                <span className="text-primary text-sm">00</span>
                <span className="text-muted-foreground">:</span>
                <span className="text-primary text-sm">00</span>
                <span className="text-muted-foreground">:</span>
                <span className="text-primary text-sm">39</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 sm:px-6 lg:px-10 py-4 sm:py-12">
        <div className="grid lg:grid-cols-[1.2fr_0.8fr] gap-8 lg:gap-2 justify-center items-center h-full">
          {/* Left: Video Preview */}
          <div className="space-y-6 pl-0 sm:pl-10">
            <div className="bg-gradient-to-br from-purple-900 via-indigo-900 to-purple-900 rounded-2xl shadow-2xl overflow-hidden aspect-video relative">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center space-y-6 px-8">
                  <div className="space-y-2">
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white/90 tracking-tight">
                      CONFERENCE
                    </h1>
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white/80">
                      WEBINAR
                    </h2>
                    <h3 className="text-5xl sm:text-6xl lg:text-7xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                      ONLINE
                    </h3>
                  </div>
                  
                  {/* Decorative elements */}
                  <div className="absolute top-10 left-10 w-20 h-20 bg-purple-500/20 rounded-full blur-2xl animate-pulse"></div>
                  <div className="absolute bottom-10 right-10 w-32 h-32 bg-pink-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 border-2 border-purple-400/20 rounded-full"></div>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="bg-card rounded-2xl shadow-lg p-6 border border-border">
              <h3 className="text-lg font-semibold text-foreground mb-3">Description about this webinar</h3>
              <p className="text-muted-foreground leading-relaxed">
                Join us for an exclusive online conference webinar featuring industry experts and thought leaders. 
                Discover the latest trends, best practices, and innovative solutions in your field. 
                Network with professionals and gain valuable insights that will help you stay ahead in your career.
              </p>
            </div>
          </div>

          {/* Right: Registration Form */}
          <div className="flex items-center justify-center">
            <div className="w-full max-w-md">
              <div className="p-2">
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-foreground mb-2">{t("webinarRegistration.registerForEvent")}</h2>
                  <p className="text-sm text-muted-foreground">
                    {t("webinarRegistration.fillFormBelow")}
                  </p>
                </div>

                <form onSubmit={handleContinue} className="space-y-6">
                  <div>
                    <Input
                      type="text"
                      value={fullName}
                      onChange={(e) => { setFullName(e.target.value.slice(0, 100)); if (errors.fullName) setErrors(p => ({ ...p, fullName: undefined })) }}
                      placeholder={t("webinarRegistration.enterFullName")}
                      variant="outlined"
                      maxLength={100}
                      error={!!errors.fullName}
                      errorText={errors.fullName}
                    />
                  </div>

                  <div>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value.slice(0, 254)); if (errors.email) setErrors(p => ({ ...p, email: undefined })) }}
                      placeholder={t("webinarRegistration.enterEmail")}
                      variant="outlined"
                      maxLength={254}
                      autoComplete="email"
                      error={!!errors.email}
                      errorText={errors.email}
                    />
                  </div>

                  <Button
                    type="submit"
                    variant={'default'}
                    className="w-full"
                  >
                    {t("webinarRegistration.continue")}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
